<script>
import api from '@utils/api'
import Communication from './communication'
import CommunicationNew from './communication-new'
import CommunicationDelete from './communication-delete'
import TablePaginator from '@components/paginator'
import userHasRolesToWrite from '@utils/userPermissions'

export default {
  name: 'Communications',
  components: {
    Communication,
    CommunicationNew,
    CommunicationDelete,
    TablePaginator,
  },
  data() {
    return {
      modelName: 'contact-communications',
      headers: [
        { text: 'contact', value: 'contact', sortable: true },
        { text: 'type', value: 'type', sortable: true },
        { text: 'value', value: 'value', sortable: true },
        { text: 'actions', value: 'actions', sortable: false },
      ],
      isLoading: true,
      rowsPerPage: [10, 25, 50],
      rolesAllowedToWrite: ['SUPERUSER', 'DICTIONARIES_OPERATOR'],
      page: 1,
      totalPages: 0,
      sortBy: 'contact',
      descending: true,
      first: false,
      last: false,
      content: [],
      pagination: {},
      totalElements: 0,
      newCommunication: false,
      editCommunication: false,
      currentCommunication: {},
    }
  },
  computed: {
    params() {
      return {
        size: this.pagination.rowsPerPage || null,
        page: this.page || null,
        sort_property: this.sortBy || null,
        sort_direction: this.descending || null,
      }
    },
    userAllowedToCreate() {
      return userHasRolesToWrite(
        this.$store.state.auth.userRole,
        this.rolesAllowedToWrite
      )
    },
    getHeaders() {
      return userHasRolesToWrite(
        this.$store.state.auth.userRole,
        this.rolesAllowedToWrite
      )
        ? this.headers
        : this.headers.slice(0, this.headers.length - 1)
    },
  },
  watch: {
    pagination: {
      handler() {
        this.$store.commit('pagination/SET', {
          name: this.modelName,
          pagination: {
            page: this.page,
            rowsPerPage: this.pagination.rowsPerPage,
            sortBy: this.sortBy,
            descending: this.descending,
          },
        })
      },
      deep: true,
    },
  },
  created() {
    this.getPaginator()
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    fetchData(params) {
      this.isLoading = true
      const payload = {
        contactId: this.$route.params.contactId,
        params: this.params,
      }
      api.getCommunications(payload).then(response => {
        this.content = response.content
        this.totalPages = response.totalPages
        this.first = response.first
        this.last = response.last
        this.page = response.number + 1
        this.pagination = {
          ...this.pagination,
          page: 1,
          rowsPerPage: response.size,
          sortBy: this.sortBy,
          descending: this.descending,
        }
        this.isLoading = false
      })
    },
    getValue(dictionaryName, value) {
      return this.$store.getters['data/getValueFromDictionary'](
        dictionaryName,
        value
      )
    },
    getContact(value) {
      const contact = this.$store.getters['data/getItemFromDictionary'](
        'contact',
        value
      )
      return `${contact.firstName || ''} ${contact.middleName ||
        ''} ${contact.lastName || ''}`
    },
    openCommunication(item) {
      this.currentCommunication = item
      this.editCommunication = true
    },
    closeCommunication() {
      this.editCommunication = false
      this.currentCommunication = {}
    },
    getPaginator() {
      const pagination = this.$store.getters['pagination/get'](this.modelName)
      if (pagination) {
        this.page = pagination.page || 1
        this.sortBy = pagination.sortBy || this.sortBy
        this.descending = pagination.descending || false
        this.pagination.rowsPerPage = pagination.rowsPerPage || 10
      } else {
        this.page = 1
        this.pagination.page = 1
        this.pagination.rowsPerPage = 10
      }
    },
    changePage(value) {
      this.page = value
      this.fetchData()
    },
    changeRowsPerPage(value) {
      if (this.pagination.rowsPerPage === value) return
      this.pagination = {
        ...this.pagination,
        rowsPerPage: value,
      }
      this.page = 1
      this.$store.dispatch('pagination/saveRowsPerPage', value)
      this.fetchData()
    },
    changeSort(header) {
      if (!header.sortable) return
      if (this.sortBy === header.value) {
        this.descending = !this.descending
      } else {
        this.sortBy = header.value
        this.descending = false
      }
      this.fetchData()
    },
    getSortClass(header) {
      return [
        header.sortable ? 'column sortable' : null,
        this.pagination.descending ? 'desc' : 'asc',
        header.value === this.pagination.sortBy ? 'active' : '',
      ]
    },
  },
}
</script>

<template>
  <v-container fluid>
    <v-toolbar 
      flat 
      color="grey lighten-5">
      <v-spacer />
      <v-btn
        v-if="userAllowedToCreate"
        color="primary"
        dark
        @click="newCommunication = true"
      >
        <v-icon>add</v-icon>
      </v-btn>
    </v-toolbar>
    <v-data-table
      :headers="headers"
      :items="content"
      :pagination="pagination"
      :loading="isLoading"
      :class="$style.sdTable"
      hide-actions
    >
      <template 
        slot="headers" 
        slot-scope="props">
        <tr>
          <th
            v-for="header in getHeaders"
            :key="header.text"
            :class="getSortClass(header)"
            style="text-align: left"
            @click="changeSort(header)"
          >
            {{ $t(`label__${header.text}`) }}
            <v-icon 
              v-if="header.sortable" 
              small>expand_less</v-icon>
          </th>
        </tr>
      </template>
      <v-progress-linear
        slot="progress"
        height="2"
        color="blue"
        background-color="#E0E0E0"
        indeterminate
      />
      <template 
        slot="items" 
        slot-scope="props">
        <tr 
          :class="$style.sdEditRow" 
          @click="openCommunication(props.item)">
          <td>{{ getContact(props.item.contact) }}</td>
          <td>{{ getValue('communicationtype', props.item.type) }}</td>
          <td>{{ props.item.value }}</td>
          <td 
            v-if="userAllowedToCreate" 
            class="text-xs-left">
            <communication-delete
              :communication-id="props.item.id"
              @communication-deleted="fetchData(params)"
            />
          </td>
        </tr>
      </template>
    </v-data-table>
    <table-paginator
      :page="page"
      :total-pages="totalPages"
      :first="first"
      :last="last"
      :rows-per-page="pagination.rowsPerPage"
      @change-page="changePage($event)"
      @change-rows-per-page="changeRowsPerPage($event)"
    />
    <communication-new
      :new-communication="newCommunication"
      @close-modal="newCommunication = false"
      @communication-added="fetchData(params)"
    />
    <communication
      :dialog="editCommunication"
      :item="currentCommunication"
      @close-modal="closeCommunication"
      @communication-updated="fetchData(params)"
    />
  </v-container>
</template>

<style lang="scss" module>
@import '@design';
.sdTable {
  border: 1px solid #dfdfdf;

  th {
    outline: none;
  }
}
.sdEditRow {
  cursor: pointer;
}
.sdBorder {
  border: 1px solid #dfdfdf;
  border-bottom: none;
}
</style>
